import dayjs from 'dayjs';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { CountryData, defaultCountries, parseCountry } from 'react-international-phone';
import { PaymentMethod, PaymentMethodRetrieve, PayrollEventRetrieve } from 'types';
import { KioskEventList } from 'types/generated';
import { isBareDialCode } from 'utils/form-validators';

export const formatPhoneNumber = (phoneNumberString: string | undefined | null): string => {
  if (!phoneNumberString) {
    return '';
  }
  const phoneNumber = parsePhoneNumberFromString(`+${phoneNumberString}`);
  if (phoneNumber && phoneNumber.isValid() && phoneNumber.country) {
    return phoneNumber.formatNational();
  }
  return phoneNumberString;
};

export const threeDecimalFormatter = (num: number): string => {
  return (Math.round(num * 1000) / 1000).toFixed(3);
};

export const numberToOrdinalFormatter = (num: number): string => {
  if (num % 10 === 1 && num % 100 !== 11) {
    return 'st';
  }
  if (num % 10 === 2 && num % 100 !== 12) {
    return 'nd';
  }
  if (num % 10 === 3 && num % 100 !== 13) {
    return 'rd';
  }

  return 'th';
};

export const getPaymentMethodName = (paymentMethod: PaymentMethod | PaymentMethodRetrieve): string => {
  if (paymentMethod.nickname) {
    return paymentMethod.nickname;
  }
  let label = '';
  if (paymentMethod.type === 'CARD') {
    label = 'Card';
    if (paymentMethod.metadata && 'brand' in paymentMethod.metadata) {
      label = `${paymentMethod.metadata.brand.charAt(0).toUpperCase()}${paymentMethod.metadata.brand.slice(
        1,
      )}`;
    }
    if (paymentMethod.metadata && 'last4' in paymentMethod.metadata) {
      label += ` ${paymentMethod.metadata.last4}`;
    }
  }
  if (paymentMethod.type === 'US BANK ACCOUNT' || paymentMethod.type === 'ACSS DEBIT') {
    label = 'Bank Account';
    if (paymentMethod.metadata && 'bankName' in paymentMethod.metadata) {
      label = paymentMethod.metadata.bankName;
    }
  }
  if (paymentMethod.type === 'BACS DEBIT') {
    label = 'Bank Account';
    if (paymentMethod.metadata && 'billingDetails' in paymentMethod.metadata) {
      label = paymentMethod.metadata.billingDetails.name;
    }
    if (paymentMethod.nickname) {
      label = paymentMethod.nickname;
    }
  }

  return label;
};

// GB mask widened so UK numbers typed with the trunk `0` are not truncated (SUP-1382).
export const phoneInputCountries = defaultCountries.map(country => {
  if (parseCountry(country).iso2 !== 'gb') {
    return country;
  }
  const gb = [...country] as CountryData;
  gb[3] = '.... .......';
  return gb;
});

export const formatPhone = (phone: string | null | undefined): string => {
  if (!phone || phone === '(___) ___-____') {
    return '';
  }
  // A bare dial code (e.g. '+1', '+44') is an untouched PhoneInput — an empty
  // value. Normalize it to '' so it is never persisted as a bogus number.
  if (isBareDialCode(phone)) {
    return '';
  }
  const parsed = parsePhoneNumberFromString(phone);
  if (parsed && parsed.isValid()) {
    return parsed.number.replace('+', '');
  }
  return phone.replace(/[^\d]/g, '');
};

export const camelCaseToTitleCaseText = (str: string): string => {
  const result = str.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const capitalize = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const truncate = (str: string, options: { maxLength?: number; suffix?: string } = {}): string => {
  const { maxLength = 10, suffix = '...' } = options;
  return str.length > maxLength ? str.substring(0, maxLength).trim() + suffix : str;
};

export const formatPayrollEventAttendees = (payrollEvent: PayrollEventRetrieve): string => {
  if (!payrollEvent.athletesAttended || !payrollEvent.firstAttendee) {
    return '';
  }

  const firstAttendee = truncate(payrollEvent.firstAttendee.fullName, { maxLength: 9 });
  const andMore = payrollEvent.athletesAttended > 1 ? ` +${payrollEvent.athletesAttended - 1}` : '';
  return `${firstAttendee}${andMore}`;
};

export const formatPayrollEventTypeCol = (payrollEvent: PayrollEventRetrieve): string => {
  const eventType = capitalizeFirstLetter(payrollEvent.genesisEventType);
  const attendees = formatPayrollEventAttendees(payrollEvent);
  return `${eventType}${attendees ? `\n${attendees}` : ''}`;
};

export const formatDatesForEvent = (event?: KioskEventList, isDesktop?: boolean): string => {
  if (!event) return '';

  const start = dayjs(`${event.startDate}T${event.startTime}`);
  const end = start.add(event.duration, 'minute');

  const dayName = start.format('dddd');
  const startFormatted = start.format('h:mmA');
  const endFormatted = end.format('h:mmA');

  if (isDesktop) return `${dayName} - ${startFormatted} to ${endFormatted}`;
  return `${dayName} ${startFormatted} - ${endFormatted}`;
};

export const getStartsIn = (event?: KioskEventList): string => {
  if (!event) return '';

  const start = dayjs(`${event.startDate}T${event.startTime}`);
  const end = start.add(event.duration, 'minute');
  const now = dayjs();

  if (!now.isBefore(end)) return 'Ended';

  if (!now.isBefore(start)) return 'Ongoing';

  const totalMins = start.diff(now, 'minute');

  if (totalMins < 60) {
    return `Starts in ${totalMins} ${totalMins === 1 ? 'Min' : 'Mins'}`;
  }

  if (totalMins < 60 * 24) {
    const hours = Math.ceil(totalMins / 60);
    return `Starts in ${hours} ${hours === 1 ? 'Hour' : 'Hours'}`;
  }

  const days = Math.ceil(totalMins / (60 * 24));
  return `Starts in ${days} ${days === 1 ? 'Day' : 'Days'}`;
};

export const formatEventNotesContentModal = (date: string): string => {
  const parsed = dayjs(date, 'YYYY-MM-DDTHH:mm:ssZ');

  const formatted = parsed.format('MM/DD/YY  h:mma');
  return formatted;
};
